/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Ortak panel/tam ekran için sınırlı durum okuma, aynı komutla tekrar ve iptal.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AssistantWorkResponseError, assistantWorkCanCancel, assistantWorkCopy, assistantWorkRecordId, assistantWorkIsActive,
  assistantWorkPath, buildAssistantWorkRequest, readAssistantWorkAcceptance,
  readAssistantWorkCancellation, readAssistantWorkPage, assistantWorkWasRejectedBeforeAcceptance,
  type AssistantWorkAcceptance, type AssistantWorkItem, type AssistantWorkRequest,
} from './assistant-work-client';

type Pending = { context: string; key: string; body: Record<string, unknown> };
type View = {
  context: string; items: AssistantWorkItem[]; error: string; loading: boolean;
  submitting: boolean; uncertain: boolean; cancelling: string[]; nextCursor: string | null;
};
const initialView = (context: string): View => ({ context, items: [], error: '', loading: false,
  submitting: false, uncertain: false, cancelling: [], nextCursor: null });
const isVisible = () => document.visibilityState !== 'hidden';

export function useAssistantWork(options: {
  scopeKey: string; conversationId?: string | number | null; enabled: boolean; language?: string;
  request: AssistantWorkRequest;
  onAccepted: (result: AssistantWorkAcceptance, message: string) => void;
  onResults: (conversationId: number | string, signal: AbortSignal) => Promise<void>;
}) {
  const conversationId = assistantWorkRecordId(options.conversationId);
  const context = JSON.stringify([options.scopeKey, conversationId]);
  const contextRef = useRef(context); contextRef.current = context;
  const optionsRef = useRef(options); optionsRef.current = options;
  const pendingRef = useRef<Pending | null>(null);
  const acceptingRef = useRef(false);
  const operations = useRef(new Set<AbortController>());
  const cancellationKeys = useRef(new Map<string, string>());
  const cancellingRef = useRef(new Set<string>());
  const resultsSeen = useRef(new Set<string>());
  const [view, setView] = useState(() => initialView(context));
  const [page, setPage] = useState<{ context: string; cursor: string | null; previous: Array<string | null> }>({ context, cursor: null, previous: [] });
  const [revision, setRevision] = useState(0);
  const visible = view.context === context ? view : initialView(context);
  const currentPage = page.context === context ? page : { context, cursor: null, previous: [] };
  const update = useCallback((scope: string, patch: Partial<View>) => {
    if (contextRef.current !== scope) return;
    setView(current => ({ ...(current.context === scope ? current : initialView(scope)), ...patch }));
  }, []);
  const refresh = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    pendingRef.current = null;
    acceptingRef.current = false;
    cancellationKeys.current.clear(); cancellingRef.current.clear(); resultsSeen.current.clear();
    setView(initialView(context));
    return () => {
      for (const controller of operations.current) controller.abort();
      operations.current.clear();
    };
  }, [context]);

  useEffect(() => {
    if (!options.enabled || !conversationId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false, failures = 0, cycles = 0;
    const current = () => !controller.signal.aborted && contextRef.current === context;
    const poll = async () => {
      if (!current() || running || !isVisible()) return;
      if (timer) clearTimeout(timer);
      running = true;
      update(context, { loading: true });
      let nextDelay: number | null = null;
      try {
        const opts = optionsRef.current;
        const query = new URLSearchParams({ conversationId: String(conversationId), limit: '25' });
        if (currentPage.cursor) query.set('cursor', currentPage.cursor);
        const response = await opts.request(assistantWorkPath('', query.toString()), { method: 'GET', signal: controller.signal });
        if (!current()) return;
        const result = readAssistantWorkPage(response, conversationId, opts.language);
        update(context, { items: result.items, nextCursor: result.nextCursor, error: '' });
        const unseen = result.items.map(item => item.resultId).filter((value): value is string => Boolean(value && !resultsSeen.current.has(value)));
        if (unseen.length && isVisible()) {
          await opts.onResults(conversationId, controller.signal);
          if (!current()) return;
          unseen.forEach(value => resultsSeen.current.add(value));
          while (resultsSeen.current.size > 100) resultsSeen.current.delete(resultsSeen.current.values().next().value!);
        }
        failures = 0;
        if (result.items.some(assistantWorkIsActive)) nextDelay = ++cycles <= 12 ? 5000 : 15000;
      } catch (error) {
        if (!current()) return;
        update(context, { error: error instanceof Error ? error.message : assistantWorkCopy(optionsRef.current.language).unconfirmed });
        // No stale content after a revoked scope or a malformed response. Retry transient reads only.
        update(context, { items: [], nextCursor: null });
        const transient = !(error instanceof AssistantWorkResponseError) || (error.status != null && error.status >= 500);
        if (transient && ++failures < 3) nextDelay = 15000;
      } finally {
        running = false;
        if (current()) {
          update(context, { loading: false });
          if (nextDelay !== null && isVisible()) timer = setTimeout(() => void poll(), nextDelay);
        }
      }
    };
    const visibility = () => {
      if (document.visibilityState === 'hidden') { if (timer) clearTimeout(timer); }
      else void poll();
    };
    document.addEventListener('visibilitychange', visibility);
    void poll();
    return () => { controller.abort(); if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', visibility); };
  }, [context, conversationId, options.enabled, currentPage.cursor, revision, update]);

  const perform = async (pending: Pending) => {
    if (acceptingRef.current || pending.context !== contextRef.current) return;
    const opts = optionsRef.current;
    if (!opts.enabled) return;
    acceptingRef.current = true;
    const controller = new AbortController(); operations.current.add(controller);
    update(pending.context, { submitting: true, uncertain: false, error: '' });
    try {
      const response = await opts.request(assistantWorkPath(), {
        method: 'POST', body: pending.body, idempotencyKey: pending.key, signal: controller.signal,
      });
      if (controller.signal.aborted || contextRef.current !== pending.context) return;
      const result = readAssistantWorkAcceptance(response, opts.language);
      if (pending.body.conversationId != null && assistantWorkRecordId(result.conversationId) !== assistantWorkRecordId(pending.body.conversationId))
        throw new AssistantWorkResponseError(assistantWorkCopy(opts.language).unconfirmed);
      pendingRef.current = null;
      setPage({ context: pending.context, cursor: null, previous: [] });
      update(pending.context, { items: result.works, nextCursor: null, uncertain: false });
      opts.onAccepted(result, String(pending.body.message));
      refresh();
    } catch (error) {
      if (controller.signal.aborted || contextRef.current !== pending.context) return;
      // Definitive input/auth/access denials release the draft; uncertain outcomes keep the same body/key.
      const rejected = assistantWorkWasRejectedBeforeAcceptance(error);
      if (rejected) pendingRef.current = null;
      update(pending.context, { uncertain: !rejected,
        error: error instanceof Error ? error.message : assistantWorkCopy(opts.language).unconfirmed });
    } finally {
      operations.current.delete(controller);
      if (contextRef.current === pending.context) {
        acceptingRef.current = false; update(pending.context, { submitting: false });
      }
    }
  };
  const submit = async (input: Record<string, unknown>) => {
    if (acceptingRef.current || !optionsRef.current.enabled) return;
    if (pendingRef.current?.context === contextRef.current) {
      update(contextRef.current, { uncertain: true, error: assistantWorkCopy(optionsRef.current.language).uncertain });
      return;
    }
    try {
      const body = buildAssistantWorkRequest(input, optionsRef.current.language);
      const pending = { context: contextRef.current, key: `assistant-work:${crypto.randomUUID()}`, body };
      pendingRef.current = pending;
      await perform(pending);
    } catch (error) {
      update(contextRef.current, { error: error instanceof Error ? error.message : assistantWorkCopy(optionsRef.current.language).invalid });
    }
  };
  const cancel = async (runId: string) => {
    if (!conversationId || !visible.items.some(item => item.runId === runId && assistantWorkCanCancel(item))
      || cancellingRef.current.has(runId) || !optionsRef.current.enabled) return;
    const opts = optionsRef.current, scope = contextRef.current;
    const controller = new AbortController(); operations.current.add(controller);
    if (!cancellationKeys.current.has(runId)) cancellationKeys.current.set(runId, `assistant-cancel:${crypto.randomUUID()}`);
    while (cancellationKeys.current.size > 100) cancellationKeys.current.delete(cancellationKeys.current.keys().next().value!);
    const key = cancellationKeys.current.get(runId)!;
    cancellingRef.current.add(runId);
    update(scope, { cancelling: [...cancellingRef.current], error: '' });
    try {
      const response = await opts.request(assistantWorkPath(`/${runId}/cancel`), {
        method: 'POST', body: { purpose: 'BUSINESS', conversationId }, idempotencyKey: key, signal: controller.signal,
      });
      if (controller.signal.aborted || contextRef.current !== scope) return;
      const result = readAssistantWorkCancellation(response, conversationId, runId, opts.language);
      setView(current => current.context === scope ? { ...current,
        items: current.items.map(item => item.runId === runId ? { ...item, ...result } : item) } : current);
      refresh();
    } catch (error) {
      if (!controller.signal.aborted && contextRef.current === scope)
        update(scope, { error: error instanceof Error ? error.message : assistantWorkCopy(opts.language).unconfirmed });
    } finally {
      operations.current.delete(controller);
      if (contextRef.current === scope) {
        cancellingRef.current.delete(runId); update(scope, { cancelling: [...cancellingRef.current] });
      }
    }
  };
  return { ...visible, submit, cancel, refresh, enabled: options.enabled, busy: () => acceptingRef.current,
    retry: () => { const pending = pendingRef.current; if (pending) void perform(pending); },
    hasPrevious: currentPage.previous.length > 0,
    previous: () => setPage({ context, cursor: currentPage.previous.at(-1) ?? null, previous: currentPage.previous.slice(0, -1) }),
    next: () => { if (visible.nextCursor)
      setPage({ context, cursor: visible.nextCursor, previous: [...currentPage.previous.slice(-99), currentPage.cursor] }); },
  };
}

export type AssistantWorkController = ReturnType<typeof useAssistantWork>;
